<template>
    <v-card flat v-if="selectedCase">
      <v-card-text>
        <!-- Part 1: Drag-and-Drop / File Input -->
        <div class="mb-4">
          <v-file-input
            v-model="rawFileInputs"
            label="Upload Case Documents / Expert Reports"
            multiple
            prepend-icon="mdi-paperclip"
            outlined
            dense
            chips
            counter
            show-size
            accept=".png,.jpeg,.jpg,.doc,.docx,.xls,.xlsx,.ppt,.pdf"
            @change="stageFiles"
            :disabled="isUploading"
          ></v-file-input>
        </div>
  
        <!-- Part 2: Staged Files Queue (Files ready to be uploaded) -->
        <v-fade-transition>
          <div v-if="stagedFiles.length > 0" class="mb-6 staged-queue-container">
            <div class="subtitle-1 font-weight-bold mb-2 d-flex align-items-center">
              <v-icon small class="mr-2" color="primary">mdi-upload-network</v-icon>
              Files Ready to Upload ({{ stagedFiles.length }})
            </div>
            
            <v-simple-table dense class="elevation-1 mb-3">
              <template v-slot:default>
                <thead>
                  <tr>
                    <th class="text-left">Original Name</th>
                    <th class="text-left" style="width: 45%;">Rename File (Optional)</th>
                    <th class="text-left">Size</th>
                    <th class="text-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(item, index) in stagedFiles" :key="index">
                    <td>
                      <span class="text-truncate d-inline-block" style="max-width: 250px;">
                        {{ item.file.name }}
                      </span>
                    </td>
                    <td>
                      <!-- User editable name field -->
                      <v-text-field
                        v-model="item.custom_name"
                        dense
                        hide-details
                        outlined
                        placeholder="e.g. medical_report"
                        suffix="."
                        :disabled="isUploading"
                      >
                        <template v-slot:append>
                          <span class="grey--text text--darken-1">{{ item.extension }}</span>
                        </template>
                      </v-text-field>
                    </td>
                    <td>{{ formatBytes(item.file.size) }}</td>
                    <td class="text-center">
                      <v-btn 
                        icon 
                        small 
                        color="error" 
                        @click="removeStagedFile(index)"
                        :disabled="isUploading"
                      >
                        <v-icon>mdi-close-circle</v-icon>
                      </v-btn>
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
  
            <!-- Upload Progress and Controls -->
            <div class="d-flex align-center flex-wrap gap-3">
              <v-btn
                color="primary"
                :loading="isUploading"
                @click="uploadStagedFiles"
                elevation="1"
              >
                <v-icon left>mdi-cloud-upload</v-icon>
                Upload ({{ stagedFiles.length }} files)
              </v-btn>
  
              <v-btn
                text
                color="grey"
                @click="clearQueue"
                :disabled="isUploading"
              >
                Clear Queue
              </v-btn>
  
              <v-spacer></v-spacer>
  
              <!-- Real-time progress bar -->
              <v-slide-x-transition>
                <div v-if="isUploading" style="width: 250px;" class="d-flex align-center">
                  <v-progress-linear
                    v-model="uploadProgress"
                    color="success"
                    height="12"
                    reactive
                    rounded
                  >
                    <template v-slot:default="{ value }">
                      <strong class="white--text text-caption">{{ Math.ceil(value) }}%</strong>
                    </template>
                  </v-progress-linear>
                </div>
              </v-slide-x-transition>
            </div>
          </div>
        </v-fade-transition>
  
        <!-- Part 3: Active/Saved Documents Table -->
        <div class="subtitle-1 font-weight-bold mb-2">
          <v-icon small class="mr-2" color="success">mdi-folder-open</v-icon>
          Saved Documents ({{ savedDocuments.length }})
        </div>
  
        <v-data-table 
          :headers="docHeaders" 
          :items="savedDocuments" 
          dense
          :loading="isDeleting"
          loading-text="Processing action..."
          class="elevation-1"
        >
          <!-- Custom Filename Display -->
          <template v-slot:item.filename="{ item }">
            <div class="font-weight-medium d-flex align-center">
              <v-icon small class="mr-2" color="blue-grey lighten-1">
                {{ getFileIcon(item.filename) }}
              </v-icon>
              {{ item.filename }}
            </div>
          </template>
  
          <!-- Dynamic Actions Slot -->
          <template v-slot:item.actions="{ item }">
            <div class="d-flex align-center">
              <!-- Download Button -->
              <v-btn 
                icon 
                small 
                :color="getTertiaryColor"
                @click="openPreview(item)"
                title="Preview Document"
                class="mr-2"
              >
                <v-icon>mdi-eye-outline</v-icon>
              </v-btn>
              <v-btn 
                icon 
                small 
                :color="getTertiaryColor"
                :href="getDownloadUrl(item)"
                download
                target="_blank"
                title="Download Document"
                class="mr-2"
              >
                <v-icon>mdi-download</v-icon>
              </v-btn>
  
              <!-- Delete Button -->
              <v-btn 
                icon 
                small 
                color="error"
                @click="confirmDelete(item)"
                title="Delete Document"
              >
                <v-icon>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
          </template>
  
          <!-- No Data State -->
          <template v-slot:no-data>
            <div class="text-center py-4 grey--text">
              No files uploaded yet for this case.
            </div>
          </template>
        </v-data-table>
      </v-card-text>
  
      <!-- Global Feedback Snackbar -->
      <v-snackbar v-model="alert.show" :color="alert.type" timeout="5000" top right>
        {{ alert.message }}
        <template v-slot:action="{ attrs }">
          <v-btn text v-bind="attrs" @click="alert.show = false">Close</v-btn>
        </template>
      </v-snackbar>
  
      <!-- Simple Delete Confirmation Dialog -->
      <v-dialog v-model="deleteDialog.show" max-width="400">
        <v-card>
          <v-card-title class="headline">Delete Document?</v-card-title>
          <v-card-text>
            Are you sure you want to permanently delete <strong>{{ deleteDialog.item ? deleteDialog.item.filename : '' }}</strong>? This action cannot be undone.
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="deleteDialog.show = false" :disabled="isDeleting">Cancel</v-btn>
            <v-btn color="error" text @click="executeDelete" :loading="isDeleting">Delete</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Document Preview Dialog -->
      <v-dialog v-model="previewDialog.show" max-width="850px" scrollable>
        <v-card v-if="previewDialog.item">
          <v-card-title class="headline grey lighten-3 py-3">
            <span class="text-truncate font-weight-medium" style="max-width: 80%;">
              Preview: {{ previewDialog.item.filename }}
            </span>
            <v-spacer></v-spacer>
            <v-btn icon @click="previewDialog.show = false">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-card-title>

          <v-card-text class="pa-0 text-center d-flex flex-column justify-center align-center grey lighten-4" style="height: 60vh; min-height: 450px;">
            
            <!-- Scenario A: Image Preview -->
            <template v-if="isImage(previewDialog.item.filename)">
              <div class="pa-4 style-preview-wrapper" style="width: 100%; height: 100%;">
                <v-img 
                  :src="previewDialog.item.file_url" 
                  max-height="100%" 
                  contain
                  class="rounded"
                >
                  <template v-slot:placeholder>
                    <v-row class="fill-height ma-0" align="center" justify="center">
                      <v-progress-circular indeterminate color="grey lighten-5"></v-progress-circular>
                    </v-row>
                  </template>
                </v-img>
              </div>
            </template>

            <!-- Scenario B: PDF Preview -->
            <template v-else-if="isPdf(previewDialog.item.filename)">
              <iframe 
                :src="previewDialog.item.file_url" 
                width="100%" 
                height="100%" 
                style="border: none; display: block;"
              ></iframe>
            </template>

            <!-- Scenario C: Office/Other Files Fallback (No native browser rendering) -->
            <template v-else>
              <div class="py-12">
                <v-icon size="100" class="mb-4" color="grey darken-1">
                  {{ getFileIcon(previewDialog.item.filename) }}
                </v-icon>
                <h3 class="title mt-2 grey--text text--darken-3">No Preview Available</h3>
                <p class="body-2 grey--text text--darken-1 px-4 mt-1">
                  This file type ({{ getExtension(previewDialog.item.filename).toUpperCase() }}) cannot be displayed directly in your browser.
                </p>
              </div>
            </template>

          </v-card-text>

          <v-divider></v-divider>

          <v-card-actions class="pa-3 grey lighten-5">
            <v-spacer></v-spacer>
            <v-btn text @click="previewDialog.show = false">
              Close
            </v-btn>
            
            <!-- Download Button inside Preview -->
            <v-btn 
              :color="getTertiaryColor"
              dark
              depressed
              :href="getDownloadUrl(previewDialog.item)"
              download
              target="_blank"
            >
              <v-icon left>mdi-download</v-icon>
              Download File
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-card>
  </template>
  
  <script>
  import axios from '@/plugins/axios';
  
  export default {
    name: 'CaseDocumentsManager',
    props: {
      selectedCase: {
        type: Object,
        required: true,
        default: () => null
      },
      emis: {
        type: String,
        required: true
      },
      getTertiaryColor: {
        type: String,
        default: 'teal'
      }
    },
    computed:{
       getAppMode(){
        return this.$store.getters.getAppMode
       },
        getAccessToken(){
            return this.$store.getters.getAccessToken
        },
        getApiUrl(){
          return this.$store.getters.getApiUrl
        },
        savedDocuments() {
            // Shorthand reference to the saved documents inside the reactive parent-provided object
        return this.selectedCase && this.selectedCase.documents ? this.selectedCase.documents : [];
      }
    },
    data() {
      return {
        rawFileInputs: [],
        stagedFiles: [],
        isUploading: false,
        isDeleting: false,
        uploadProgress: 0,
        previewDialog: {
          show: false,
          item: null
        },
        
        docHeaders: [
          { text: 'Document Name', value: 'filename', align: 'start', sortable: true },
          { text: 'Uploaded By', value: 'uploaded_by', align: 'start' },
          { text: 'Date Added', value: 'created_at', align: 'start' },
          { text: 'Actions', value: 'actions', align: 'center', sortable: false }
        ],
  
        // Dialog & Snackbar Controls
        alert: {
          show: false,
          message: '',
          type: 'info'
        },
        deleteDialog: {
          show: false,
          item: null
        }
      };
    },
    watch: {
      // If the active case changes, clear out any staging items to prevent user mistakes
      'selectedCase.id'() {
        this.clearQueue();
      }
    },
    methods: {
      // 1. Process files dropped into input and stage them
      stageFiles() {
        if (!this.rawFileInputs || this.rawFileInputs.length === 0) return;
  
        this.rawFileInputs.forEach(file => {
          // Extract original base name and extension
          const dotIndex = file.name.lastIndexOf('.');
          const baseName = dotIndex !== -1 ? file.name.substring(0, dotIndex) : file.name;
          const extension = dotIndex !== -1 ? file.name.substring(dotIndex + 1) : '';
  
          // Prevent duplication in the staging queue
          const alreadyStaged = this.stagedFiles.some(f => f.file.name === file.name && f.file.size === file.size);
          
          if (!alreadyStaged) {
            this.stagedFiles.push({
              file: file,
              custom_name: baseName, // Default to original base name (user can change this)
              extension: extension
            });
          }
        });
  
        // Reset file input component itself so user can interact with it again
        this.rawFileInputs = [];
      },
  
      // 2. Remove file from staging queue
      removeStagedFile(index) {
        this.stagedFiles.splice(index, 1);
      },
  
      // 3. Clear all staged files
      clearQueue() {
        this.stagedFiles = [];
        this.uploadProgress = 0;
      },
  
      // 4. Send files to Laravel using Multipart Form-Data
      async uploadStagedFiles() {
        if (this.stagedFiles.length === 0) return;
  
        this.isUploading = true;
        this.uploadProgress = 0;
  
        const formData = new FormData();
        formData.append('emis', this.emis);
        const reference = this.selectedCase.internalRef ? this.selectedCase.internalRef : this.selectedCase.invoice_number;
        formData.append('internalRef', reference);
        formData.append('mode', this.getAppMode);
  
        // Construct nested array fields for Laravel controller model compatibility
        this.stagedFiles.forEach((item, index) => {
          formData.append(`documents[${index}][file]`, item.file);
          // Append customized name (without manual extension addition, Laravel handles extension)
          formData.append(`documents[${index}][custom_name]`, item.custom_name);
        });
  
        try {
            const response = await axios.postDataWithToken(
                '/auth/save-or-update-case-documents', 
                formData, 
                this.getAccessToken,
                {
                onUploadProgress: (progressEvent) => {
                    if (progressEvent.total) {
                    this.uploadProgress = (progressEvent.loaded / progressEvent.total) * 100;
                    }
                }
                }
            );
                
            // Check if the request failed gracefully via our helper's catch fallback
            if (response && (response.status >= 400 || response.data?.error)) {
                const errorMsg = response.data?.error || response.data?.message || 'Error uploading files.';
                this.showAlert(errorMsg, 'error');
                return;
            }
          
            // Notify parent to refresh
              if(response && response.success){
                this.$emit('refresh-documents',response.docs);
              }
            this.showAlert('Documents uploaded successfully!', 'success');
            this.clearQueue();

            } catch (error) {
            // Fallback for any catastrophic execution issues
            this.showAlert('A network error occurred during upload.', 'error');
            } finally {
          this.isUploading = false;
        }
      },
  
      // 5. Delete confirmation handling
      confirmDelete(item) {
        this.deleteDialog.item = item;
        this.deleteDialog.show = true;
      },
  
      // 6. Request backend deletion (to be completed)
      async executeDelete() {
        if (!this.deleteDialog.item) return;
  
        this.isDeleting = true;
        const targetItem = this.deleteDialog.item;
        //console.log(targetItem)
        try {
          // Replace endpoint structure to match your API layout
          const reference = this.selectedCase.internalRef ? this.selectedCase.internalRef : this.selectedCase.invoice_number;
          const response = await axios.postDataWithToken("/auth/delete-case-document",{docToDelete:targetItem, caseInternalRef: reference, mode: this.getAppMode},this.getAccessToken);
          this.showAlert('Document deleted successfully.', 'success');
          
          // Notify parent to refresh
          if(response && response.success){
            this.$emit('refresh-documents',response.docs);
          }
          
        } catch (error) {
          const errorMsg = 'Failed to delete the document.';
          this.showAlert(errorMsg, 'error');
        } finally {
          this.isDeleting = false;
          this.deleteDialog.show = false;
          this.deleteDialog.item = null;
        }
      },
  
      // Utility: Format file sizes
      formatBytes(bytes, decimals = 2) {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const dm = decimals < 0 ? 0 : decimals;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
      },
  
      // Utility: Toast alerts
      showAlert(message, type = 'info') {
        this.alert.message = message;
        this.alert.type = type;
        this.alert.show = true;
      },
  
      // Utility: Return decorative UI icon based on extension
      getFileIcon(filename) {
        if (!filename) return 'mdi-file';
        const ext = filename.split('.').pop().toLowerCase();
        switch (ext) {
          case 'pdf': return 'mdi-file-pdf-box';
          case 'doc':
          case 'docx': return 'mdi-file-word';
          case 'xls':
          case 'xlsx': return 'mdi-file-excel';
          case 'ppt':
          case 'pptx': return 'mdi-file-powerpoint';
          case 'png':
          case 'jpg':
          case 'jpeg': return 'mdi-file-image';
          default: return 'mdi-file-document';
        }
      },
     // Generates the download URL pointing directly to your Laravel API endpoint
  getDownloadUrl(item) {
    // Encodes the path safely to be used as a URL parameter
    const safePath = encodeURIComponent(item.id); 
    
    // Replace with your API domain if it's different from the frontend app
    return `${this.getApiUrl}/cases/documents/download/${safePath}`;
  },

      // 2. Open Preview Handler
  openPreview(item) {
    this.previewDialog.item = item;
    this.previewDialog.show = true;
  },

  // Helper to extract extension
  getExtension(filename) {
    if (!filename) return '';
    return filename.split('.').pop().toLowerCase();
  },

  // Helper to check if file is an Image
  isImage(filename) {
    const ext = this.getExtension(filename);
    return ['png', 'jpg', 'jpeg', 'gif', 'webp'].includes(ext);
  },

  // Helper to check if file is a PDF
  isPdf(filename) {
    const ext = this.getExtension(filename);
    return ext === 'pdf';
  }
    }
  };
  </script>
  
  <style scoped>
  .staged-queue-container {
    border: 1px dashed var(--v-secondary-base, #ccc);
    border-radius: 8px;
    padding: 16px;
    background-color: rgba(0, 0, 0, 0.02);
  }
  .gap-3 {
    gap: 12px;
  }
  </style>